import { useMemo } from 'react';
import moment from 'moment';

import useStores from 'shared/hooks/use-stores';
import { getAgeRestriction } from 'shared/helpers/age-restrictions';
import useGeoinfo from 'src/hooks/use-geoinfo';
import { useAge } from 'src/components/modals/age-restriction/hooks';

type LocationInfo = { country: string; region: string };

type Dispensary = {
  location?: { country?: string | null; state?: string | null } | null;
  storeSettings?: { enableStorefrontAgeGate?: boolean | null } | null;
};

function normalizeCountry(raw: string | null | undefined): string {
  if (raw === 'United States') return 'US';
  if (raw === 'Canada') return 'CA';
  if (raw) return raw;
  return 'US';
}

// Mirrors apps/marketplace/src/components/modals/age-restriction/index.jsx getLocationInfo:
// prefer the dispensary's location, fall back to user geoinfo.
function getLocationInfo(dispensary: Dispensary | null | undefined, geoinfo: LocationInfo | null): LocationInfo | null {
  if (dispensary?.location?.state) {
    return { country: normalizeCountry(dispensary.location.country), region: dispensary.location.state };
  }
  return geoinfo;
}

/**
 * Returns true when the user has cleared the age gate (or it does not apply).
 * Prevents the order-type takeover from stacking on top of the age gate.
 * Mirrors the open-condition in age-restriction/index.jsx and may be replaced by a
 * shared signal in a future refactor (see plan ticket 2 follow-ups).
 */
export function useIsAgeGatePassed(dispensary: Dispensary | null | undefined): boolean {
  const { user, UI } = useStores();
  const [age] = useAge();
  const [geoinfo] = useGeoinfo();

  const locationInfo = useMemo(() => getLocationInfo(dispensary, geoinfo as LocationInfo | null), [
    dispensary,
    geoinfo,
  ]);

  // ECOMM-10941: on a `/stores/` storefront with its own age gate (enableStorefrontAgeGate
  // off), Dutchie's gate never renders, so age stays 0 and would block the takeover forever.
  // Treat as passed — storefront only; embedded/app/kiosk keep their gating.
  if (UI.isStoreFront && !dispensary?.storeSettings?.enableStorefrontAgeGate) {
    return true;
  }

  if (!locationInfo) {
    return true;
  }

  const { recMinAge, forceSessionStorage } = getAgeRestriction(locationInfo);
  if (!recMinAge) {
    return true;
  }

  let effectiveAge = age;
  if (forceSessionStorage) {
    const sessionOnlyOverride = user.getSessionOnlyAgeOverride?.();
    effectiveAge =
      sessionOnlyOverride && moment(sessionOnlyOverride.expires).isAfter(moment()) ? sessionOnlyOverride.age : 0;
  }

  return effectiveAge >= recMinAge;
}
